import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Wrench, Plus, Trash2, ArrowLeft, Save, Printer, Download, Send, X, Check, Ban, Factory, ShieldCheck, Tag } from 'lucide-react';
import CustomerDedupHints from '../../components/CustomerDedupHints';
import QuickAddCustomerButton from '../../components/QuickAddCustomerDialog';
import { toast } from 'sonner';
import { DocHistoryPanel } from '../../components/orders/DocHistoryPanel';
import { RepairProductCard, BrandDatalist } from '../../components/orders/RepairProductCard';
import { TradeInLeadBanner } from '../../components/orders/TradeInLeadBanner';
import DocumentChainStrip from '../../components/DocumentChainStrip';
import LockedDocBanner from '../../components/LockedDocBanner';
import RepairMachinesCard from '../../components/orders/RepairMachinesCard';
import LinkedPosCard from '../../components/purchasing/LinkedPosCard';
import EvidencePhotoGallery from '../../components/EvidencePhotoGallery';

const STATUS_META = {
  Draft:      { bg: 'bg-slate-100 text-slate-700 border-slate-200' },
  Received:   { bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  Diagnosing: { bg: 'bg-amber-100 text-amber-800 border-amber-200' },
  Repairing:  { bg: 'bg-orange-100 text-orange-800 border-orange-200' },
  Testing:    { bg: 'bg-purple-100 text-purple-800 border-purple-200' },
  Returned:   { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  Closed:     { bg: 'bg-slate-900 text-white border-slate-900' },
  Canceled:   { bg: 'bg-red-100 text-red-800 border-red-200' },
};

const newPart = () => ({ id: `tmp-${Math.random().toString(36).slice(2)}`, item_sku: '', description: '', quantity: 1, unit_cost: 0, notes: '' });

const RepairOrderDetailPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const dupId = params.get('dup');
  const highlightMachine = params.get('machine');
  const isNew = !id || id === 'new';
  const [ro, setRo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState({
    customer_id: '',
    intake_date: new Date().toISOString().slice(0, 10),
    is_competitor: false, competitor_brand: '', product_type_id: '',
    product_sku: '', product_name: '', product_serial: '', product_model: '',
    original_purchase_date: '', warranty_covered: false,
    reported_issue: '', diagnosis: '', resolution_notes: '',
    labor_hours: 0, labor_rate: 0,
    parts: [],
    machines: [],
    rma_number: '', estimated_cost: 0, customer_authorized_amount: 0, priority: 'Normal',
    received_carrier: '', received_tracking_number: '', technician_name: '',
    return_carrier: '', return_tracking_number: '',
    estimated_completion_date: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, it, pt] = await Promise.all([api().get('/customers'), api().get('/items'), api().get('/product-types?level=product')]);
      setCustomers(c.data || []); setItems(it.data || []); setProducts((pt.data || []).filter(p => p.active_status !== false));
      api().get('/repair-orders-meta/competitor-brands').then(r => setBrands(r.data || [])).catch(() => {});
      if (!isNew || dupId) {
        const r = await api().get(`/repair-orders/${isNew ? dupId : id}`);
        if (isNew) toast.success(`Copied from ${r.data.ro_number}. Review, then save the new Draft`);
        setRo(isNew ? null : r.data);
        setForm({
          customer_id: r.data.customer_id,
          intake_date: isNew ? new Date().toISOString().slice(0, 10) : r.data.intake_date,
          is_competitor: !!r.data.is_competitor,
          competitor_brand: r.data.competitor_brand || '',
          product_type_id: r.data.product_type_id || '',
          product_sku: r.data.product_sku || '',
          product_name: r.data.product_name || '',
          product_serial: r.data.product_serial || '',
          product_model: r.data.product_model || '',
          original_purchase_date: r.data.original_purchase_date || '',
          warranty_covered: !!r.data.warranty_covered,
          reported_issue: r.data.reported_issue || '',
          diagnosis: r.data.diagnosis || '',
          resolution_notes: r.data.resolution_notes || '',
          labor_hours: r.data.labor_hours || 0,
          labor_rate: r.data.labor_rate || 0,
          parts: (r.data.parts || []).map(p => ({ ...p })),
          machines: (r.data.machines || []).map(m => (isNew ? { ...m, id: null, finished_unit_id: null } : { ...m })),
          rma_number: r.data.rma_number || '',
          estimated_cost: r.data.estimated_cost || 0,
          customer_authorized_amount: r.data.customer_authorized_amount || 0,
          priority: r.data.priority || 'Normal',
          received_carrier: r.data.received_carrier || '',
          received_tracking_number: r.data.received_tracking_number || '',
          technician_name: r.data.technician_name || '',
          return_carrier: r.data.return_carrier || '',
          return_tracking_number: r.data.return_tracking_number || '',
          estimated_completion_date: r.data.estimated_completion_date || '',
          notes: r.data.notes || '',
        });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
  }, [api, id, isNew, dupId]);
  useEffect(() => { load(); }, [load]);

  const editable = isNew || (ro && !['Closed', 'Canceled'].includes(ro.status));
  const addPart = () => setForm({ ...form, parts: [...form.parts, newPart()] });
  const removePart = (i) => setForm({ ...form, parts: form.parts.filter((_, idx) => idx !== i) });
  const updatePart = (i, patch) => setForm({ ...form, parts: form.parts.map((p, idx) => idx === i ? { ...p, ...patch } : p) });

  const fillPartFromItem = (i, sku) => {
    const it = items.find(x => x.item_sku === sku);
    if (!it) return;
    updatePart(i, { item_sku: it.item_sku, description: it.item_name, unit_cost: Number(it.base_cost || it.unit_cost || 0) });
  };


  const partsCost = (form.parts || []).reduce((a, p) => a + Number(p.quantity || 0) * Number(p.unit_cost || 0), 0);
  const laborCost = Number(form.labor_hours || 0) * Number(form.labor_rate || 0);
  const totalCost = partsCost + laborCost;

  const save = async () => {
    if (!form.customer_id && !((form.customer_name || '').trim())) { toast.error('Pick a customer or type a name'); return; }
    if (form.is_competitor && !(form.competitor_brand || '').trim()) { toast.error('Competitor brand is required'); return; }
    if (!form.product_name.trim()) { toast.error('Product name is required'); return; }
    if (!form.reported_issue.trim()) { toast.error('Reported issue is required'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        intake_date: form.intake_date,
        is_competitor: !!form.is_competitor,
        competitor_brand: form.is_competitor ? (form.competitor_brand || '').trim() : null,
        product_type_id: form.is_competitor ? null : (form.product_type_id || null),
        product_sku: form.is_competitor ? null : (form.product_sku || null),
        product_name: form.product_name,
        product_serial: form.product_serial || null,
        product_model: form.product_model || null,
        original_purchase_date: form.original_purchase_date || null,
        warranty_covered: !!form.warranty_covered,
        reported_issue: form.reported_issue,
        diagnosis: form.diagnosis || null,
        resolution_notes: form.resolution_notes || null,
        labor_hours: Number(form.labor_hours || 0),
        labor_rate: Number(form.labor_rate || 0),
        parts: (form.parts || []).filter(p => (p.description || '').trim()).map(p => ({
          item_sku: p.item_sku || null,
          description: p.description,
          quantity: Number(p.quantity || 0),
          unit_cost: Number(p.unit_cost || 0),
          notes: p.notes || null,
        })),
        machines: (form.machines || []).map(m => ({
          id: m.id && !String(m.id).startsWith('tmp-') ? m.id : null,
          serial_number: m.serial_number || null, model: m.model || null,
          product_name: m.product_name || null, notes: m.notes || null,
          is_competitor: !!m.is_competitor, brand: m.is_competitor ? (m.brand || null) : null,
          product_sku: m.is_competitor ? null : (m.product_sku || null), product_type_id: m.is_competitor ? null : (m.product_type_id || null),
        })),
        rma_number: form.rma_number || null,
        estimated_cost: Number(form.estimated_cost || 0),
        customer_authorized_amount: Number(form.customer_authorized_amount || 0),
        priority: form.priority || 'Normal',
        received_carrier: form.received_carrier || null,
        received_tracking_number: form.received_tracking_number || null,
        technician_name: form.technician_name || null,
        return_carrier: form.return_carrier || null,
        return_tracking_number: form.return_tracking_number || null,
        estimated_completion_date: form.estimated_completion_date || null,
        notes: form.notes || null,
      };
      if (isNew) {
        const r = await api().post('/repair-orders', payload);
        toast.success(`${r.data.ro_number} created as Draft`);
        navigate(`/orders/repairs/${r.data.id}`, { replace: true });
      } else {
        const r = await api().put(`/repair-orders/${id}`, payload);
        setRo(r.data); setForm(f => ({ ...f, machines: (r.data.machines || []).map(m => ({ ...m })) })); toast.success(`${r.data.ro_number} saved`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const act = async (action, extra = {}) => {
    setActing(true);
    try { const r = await api().post(`/repair-orders/${id}/${action}`, extra); setRo(r.data); toast.success(`${r.data.ro_number} → ${r.data.status}`); }
    catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); } finally { setActing(false); }
  };

  const spawnWo = async () => {
    const bomId = window.prompt('Optional: paste a BOM ID to link, or leave blank for a generic repair Work Order:');
    try {
      const r = await api().post(`/repair-orders/${id}/spawn-work-order`, { bom_id: bomId || null });
      setRo(r.data); toast.success(`Linked ${r.data.work_order_number}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Spawn failed'); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete Draft ${ro.ro_number}? This cannot be undone.`)) return;
    try { await api().delete(`/repair-orders/${id}`); toast.success('Draft deleted'); navigate('/orders/repairs'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const printRo = () => {
    const base = api().defaults.baseURL; const token = api().defaults.headers?.Authorization;
    const w = window.open('', '_blank'); if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    fetch(`${base}/repair-orders/${id}/print`, { headers: { Authorization: token } })
      .then(r => r.text()).then(html => { w.document.open(); w.document.write(html); w.document.close(); })
      .catch(() => { w.close(); toast.error('Print load failed'); });
  };

  const printThermalLabel = () => {
    const base = api().defaults.baseURL; const token = api().defaults.headers?.Authorization;
    const w = window.open('', '_blank'); if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    fetch(`${base}/repair-orders/${id}/thermal-label`, { headers: { Authorization: token } })
      .then(r => r.text())
      .then(html => { w.document.open(); w.document.write(html); w.document.close(); setTimeout(() => { try { w.focus(); w.print(); } catch { /* ignore */ } }, 400); })
      .catch(() => { w.close(); toast.error('Label load failed'); });
  };

  const setMachineStatus = async (mid, status) => {
    try {
      const r = await api().post(`/repair-orders/${id}/machines/${mid}/status`, { status });
      setRo(r.data);
      const byId = Object.fromEntries((r.data.machines || []).map(m => [m.id, m]));
      setForm(f => ({ ...f, machines: f.machines.map(m => byId[m.id] ? { ...m, status: byId[m.id].status, status_at: byId[m.id].status_at, status_by_name: byId[m.id].status_by_name, status_history: byId[m.id].status_history } : m) }));
      toast.success(`${byId[mid]?.serial_number || 'Machine'} → ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Status update failed'); }
  };

  const machineCounts = (ro?.machines || []).reduce((a, m) => ({ ...a, [m.status || 'Received']: (a[m.status || 'Received'] || 0) + 1 }), {});
  const machineTotal = (ro?.machines || []).length;
  const allMachines = (st) => machineTotal > 0 && machineCounts[st] === machineTotal;
  const suggestion = !ro ? null
    : (machineTotal > 0 && (machineCounts.Done || 0) + (machineCounts.Returned || 0) === machineTotal && ro.status === 'Repairing') ? { action: 'test', text: 'All machines are Done. Move this repair order to Testing?', btn: 'Move to Testing' }
    : (allMachines('Returned') && ro.status === 'Testing') ? { action: 'return', text: 'All machines are Returned. Mark this repair order Returned?', btn: 'Mark Returned' }
    : ((machineCounts['In Repair'] || 0) > 0 && ro.status === 'Diagnosing') ? { action: 'repair', text: 'A machine is In Repair. Move this repair order to Repairing?', btn: 'Start Repairing' }
    : null;

  const printMachineStickers = (machineId) => {
    const base = api().defaults.baseURL; const token = api().defaults.headers?.Authorization;
    const w = window.open('', '_blank'); if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    const qs = new URLSearchParams({ origin: window.location.origin, ...(machineId ? { machine_id: machineId } : {}) });
    fetch(`${base}/repair-orders/${id}/machine-stickers?${qs}`, { headers: { Authorization: token } })
      .then(r => { if (!r.ok) throw new Error(); return r.text(); })
      .then(html => { w.document.open(); w.document.write(html); w.document.close(); setTimeout(() => { try { w.focus(); w.print(); } catch { /* ignore */ } }, 400); })
      .catch(() => { w.close(); toast.error('Sticker load failed'); });
  };

  const download = async (fmt) => {
    try {
      const res = await api().get(fmt === 'pdf' ? `/repair-orders/${id}/pdf` : `/repair-orders/${id}/export.${fmt}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : (fmt === 'json' ? 'application/json' : 'text/csv') });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${ro.ro_number}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error('Download failed'); }
  };

  const emailRo = async () => {
    try { const r = await api().post(`/repair-orders/${id}/email`); toast.success(`Emailed to ${r.data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
  };

  const statusPill = ro && (
    <Badge className={`${(STATUS_META[ro.status] || STATUS_META.Draft).bg} border rounded-sm text-xs uppercase tracking-wider`} data-testid="ro-status-pill">
      {ro.status}
    </Badge>
  );

  return (
    <div className="space-y-4" data-testid="repair-order-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders/repairs')}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wrench className="w-6 h-6 text-slate-700" />
              {isNew ? 'New Lyphline Repair Order' : (ro?.ro_number ? `Lyphline Repair Order ${ro.ro_number}` : 'Loading…')}
              {statusPill}
              {ro?.warranty_covered && <Badge className="bg-blue-100 text-blue-800 border-blue-200 border rounded-sm text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> WARRANTY</Badge>}
            </h1>
            {ro && (
              <p className="text-xs text-slate-500 mt-1">
                {ro.customer_name_snapshot} · {ro.is_competitor && <span className="text-red-700 font-semibold" data-testid="ro-header-competitor">Competitor: {ro.competitor_brand} · </span>}{ro.product_name}{ro.product_sku && <> · <span className="mono">{ro.product_sku}</span></>}{ro.product_serial && <> · SN <span className="mono">{ro.product_serial}</span></>}
                {ro.work_order_number && <> · WO <strong className="mono">{ro.work_order_number}</strong></>}
                {machineTotal > 0 && <span data-testid="ro-machine-summary"> · Machines: {['Received', 'In Repair', 'Done', 'Returned'].filter(st => machineCounts[st]).map(st => `${machineCounts[st]} ${st}`).join(', ')} <strong>({(machineCounts.Done || 0) + (machineCounts.Returned || 0)} of {machineTotal} Done)</strong></span>}
              </p>
            )}
          </div>
        </div>
        {ro && (
          <div className="flex items-center gap-1 flex-wrap">
            <Button variant="outline" size="sm" onClick={printRo} className="rounded-sm" data-testid="ro-print-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>
            <Button variant="outline" size="sm" onClick={printThermalLabel} className="rounded-sm bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="ro-thermal-label-btn"><Tag className="w-3.5 h-3.5 mr-1" /> Label</Button>
            <Button variant="outline" size="sm" onClick={() => printMachineStickers()} className="rounded-sm bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid="ro-machine-stickers-btn"><Tag className="w-3.5 h-3.5 mr-1" /> Machine Stickers (2×4)</Button>
            <Button variant="outline" size="sm" onClick={() => download('pdf')} className="rounded-sm" data-testid="ro-export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => download('json')} className="rounded-sm" data-testid="ro-export-json-btn"><Download className="w-3.5 h-3.5 mr-1" /> JSON</Button>
            <Button variant="outline" size="sm" onClick={() => download('csv')} className="rounded-sm" data-testid="ro-export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={emailRo} className="rounded-sm" data-testid="ro-email-btn"><Send className="w-3.5 h-3.5 mr-1" /> Email</Button>
            {ro.status === 'Draft'      && <Button size="sm" onClick={() => act('receive')}  disabled={acting} className="bg-blue-600 hover:bg-blue-700 rounded-sm"       data-testid="ro-receive-btn"><Check className="w-3.5 h-3.5 mr-1" /> Mark Received</Button>}
            {ro.status === 'Received'   && <Button size="sm" onClick={() => act('diagnose')} disabled={acting} className="bg-amber-600 hover:bg-amber-700 rounded-sm"     data-testid="ro-diagnose-btn"><Check className="w-3.5 h-3.5 mr-1" /> Start Diagnosis</Button>}
            {ro.status === 'Diagnosing' && <Button size="sm" onClick={() => act('repair')}   disabled={acting} className="bg-orange-600 hover:bg-orange-700 rounded-sm"   data-testid="ro-repair-btn"><Check className="w-3.5 h-3.5 mr-1" /> Start Repair</Button>}
            {['Diagnosing', 'Repairing'].includes(ro.status) && !ro.work_order_id && <Button size="sm" variant="outline" onClick={spawnWo} disabled={acting} className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-sm" data-testid="ro-spawn-wo-btn"><Factory className="w-3.5 h-3.5 mr-1" /> Spawn Work Order</Button>}
            {ro.status === 'Repairing'  && <Button size="sm" onClick={() => act('test')}     disabled={acting} className="bg-purple-600 hover:bg-purple-700 rounded-sm"   data-testid="ro-test-btn"><Check className="w-3.5 h-3.5 mr-1" /> Move to Testing</Button>}
            {ro.status === 'Testing'    && <Button size="sm" onClick={() => act('return')}   disabled={acting} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="ro-return-btn"><Check className="w-3.5 h-3.5 mr-1" /> Return to Customer</Button>}
            {ro.status === 'Returned'   && <Button size="sm" onClick={() => act('close')}    disabled={acting} className="bg-slate-900 hover:bg-slate-800 rounded-sm"     data-testid="ro-close-btn"><Check className="w-3.5 h-3.5 mr-1" /> Close</Button>}
            {!['Closed', 'Canceled'].includes(ro.status) && <Button size="sm" variant="outline" onClick={() => act('cancel', { reason: window.prompt('Cancel reason (optional):') || '' })} disabled={acting} className="border-red-200 text-red-700 hover:bg-red-50 rounded-sm" data-testid="ro-cancel-btn"><Ban className="w-3.5 h-3.5 mr-1" /> Cancel</Button>}
            {ro.status === 'Draft' && <Button size="sm" variant="ghost" onClick={remove} className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm" data-testid="ro-delete-btn"><Trash2 className="w-3.5 h-3.5" /></Button>}
          </div>
        )}
      </div>

      {!isNew && <DocumentChainStrip api={api} docType="repair_order" docId={id} />}
      {ro && !editable && <LockedDocBanner doc={ro} label="Lyphline Repair Order" onDuplicate={() => navigate(`/orders/repairs/new?dup=${id}`)} />}

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Customer + Intake</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <Label>Customer *</Label>
            <div className="flex gap-2">
              <div className="flex-1">
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })} disabled={!editable}>
              <SelectTrigger className="rounded-sm" data-testid="ro-customer-select"><SelectValue placeholder="Pick a customer…" /></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
            </Select>
              </div>
              <QuickAddCustomerButton api={api} disabled={!editable} testId="ro-new-customer" onCreated={(c) => { setCustomers(prev => [...prev, c].sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''))); setForm(f => ({ ...f, customer_id: c.id })); }} />
            </div>
          </div>
          <div><Label>Intake Date</Label><Input type="date" value={form.intake_date} onChange={(e) => setForm({ ...form, intake_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="ro-intake-date" /></div>
          <div><Label>Est. Completion</Label><Input type="date" value={form.estimated_completion_date} onChange={(e) => setForm({ ...form, estimated_completion_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="ro-est-completion" /></div>
          <div><Label>RMA #</Label><Input value={form.rma_number} onChange={(e) => setForm({ ...form, rma_number: e.target.value })} placeholder="Customer RMA number" disabled={!editable} className="rounded-sm mono" data-testid="ro-rma-number" /></div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority || 'Normal'} onValueChange={(v) => setForm({ ...form, priority: v })} disabled={!editable}>
              <SelectTrigger className="rounded-sm" data-testid="ro-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Low">Low</SelectItem><SelectItem value="Normal">Normal</SelectItem><SelectItem value="Rush">Rush</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Technician Assigned</Label><Input value={form.technician_name} onChange={(e) => setForm({ ...form, technician_name: e.target.value })} placeholder="Name of lead tech" disabled={!editable} className="rounded-sm" data-testid="ro-technician" /></div>
          <div><Label>Received Carrier</Label><Input value={form.received_carrier} onChange={(e) => setForm({ ...form, received_carrier: e.target.value })} placeholder="Inbound carrier (UPS, FedEx…)" disabled={!editable} className="rounded-sm" data-testid="ro-received-carrier" /></div>
          <div><Label>Received Tracking #</Label><Input value={form.received_tracking_number} onChange={(e) => setForm({ ...form, received_tracking_number: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="ro-received-tracking" /></div>
          <div><Label>Estimated Cost ($)</Label><Input type="number" min="0" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="ro-estimated-cost" /></div>
          <div><Label>Customer-Authorized Amount ($)</Label><Input type="number" min="0" step="0.01" value={form.customer_authorized_amount} onChange={(e) => setForm({ ...form, customer_authorized_amount: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="ro-authorized-amount" /></div>
          <div className="col-span-2 md:col-span-4 flex items-center gap-2">
            <Checkbox checked={!!form.warranty_covered} onCheckedChange={(v) => setForm({ ...form, warranty_covered: !!v })} disabled={!editable} data-testid="ro-warranty-checkbox" id="ro-warranty-checkbox" />
            <Label htmlFor="ro-warranty-checkbox" className="cursor-pointer">Covered under warranty — total will be waived on the printed order</Label>
          </div>
        </CardContent>
      </Card>

      {!isNew && ro && <TradeInLeadBanner key={`${ro.updated_at}`} roId={id} />}
      <RepairProductCard form={form} setForm={setForm} editable={editable} products={products} />
      <BrandDatalist brands={brands} />

      {!isNew && <LinkedPosCard api={api} param="repair_order_id" id={id} testId="ro-linked-pos" />}
      {suggestion && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-sm border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900" data-testid="ro-machine-suggestion">
          <span data-testid="ro-machine-suggestion-text">{suggestion.text}</span>
          <Button size="sm" onClick={() => act(suggestion.action)} disabled={acting} className="bg-emerald-700 hover:bg-emerald-800 rounded-sm" data-testid="ro-machine-suggestion-btn">{suggestion.btn}</Button>
        </div>
      )}
      <RepairMachinesCard
        onStatus={setMachineStatus}
        highlightId={highlightMachine}
        machines={form.machines || []}
        products={products}
        onChange={(machines) => setForm(f => ({ ...f, machines }))}
        editable={editable}
        onPrintSticker={printMachineStickers}
        savedIds={new Set((ro?.machines || []).map(m => m.id))}
      />

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Issue + Diagnosis</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-1 gap-3">
          <div><Label>Reported Issue *</Label><Textarea rows={2} value={form.reported_issue} onChange={(e) => setForm({ ...form, reported_issue: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="ro-reported-issue" /></div>
          <div><Label>Diagnosis</Label><Textarea rows={3} value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="ro-diagnosis" placeholder="Technician's findings…" /></div>
          <div><Label>Resolution Notes</Label><Textarea rows={2} value={form.resolution_notes} onChange={(e) => setForm({ ...form, resolution_notes: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="ro-resolution" placeholder="What was done to resolve…" /></div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Parts Used</CardTitle>
          {editable && <Button size="sm" variant="outline" onClick={addPart} className="rounded-sm" data-testid="ro-add-part-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Part</Button>}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Item SKU</TableHead><TableHead>Description *</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Unit $</TableHead>
              <TableHead className="text-right">Line Total</TableHead>
              {editable && <TableHead></TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {form.parts.map((p, i) => {
                const lt = (Number(p.quantity || 0) * Number(p.unit_cost || 0)).toFixed(2);
                return (
                  <TableRow key={p.id} data-testid={`ro-part-${i}`}>
                    <TableCell className="min-w-[180px]">
                      <Select value={p.item_sku || ''} onValueChange={(v) => fillPartFromItem(i, v)} disabled={!editable}>
                        <SelectTrigger className="rounded-sm text-xs"><SelectValue placeholder="— pick item —" /></SelectTrigger>
                        <SelectContent className="max-h-72">{items.map(it => <SelectItem key={it.id} value={it.item_sku}>{it.item_sku} — {it.item_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="min-w-[220px]"><Input value={p.description} onChange={(e) => updatePart(i, { description: e.target.value })} disabled={!editable} className="rounded-sm text-xs" data-testid={`ro-part-desc-${i}`} /></TableCell>
                    <TableCell className="text-right w-20"><Input type="number" min="0" step="0.01" value={p.quantity} onChange={(e) => updatePart(i, { quantity: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`ro-part-qty-${i}`} /></TableCell>
                    <TableCell className="text-right w-24"><Input type="number" min="0" step="0.01" value={p.unit_cost} onChange={(e) => updatePart(i, { unit_cost: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`ro-part-cost-${i}`} /></TableCell>
                    <TableCell className="text-right mono font-semibold">${lt}</TableCell>
                    {editable && <TableCell className="w-10"><Button variant="ghost" size="icon" onClick={() => removePart(i)} className="text-red-600 h-7 w-7"><X className="w-3.5 h-3.5" /></Button></TableCell>}
                  </TableRow>
                );
              })}
              {!form.parts.length && <TableRow><TableCell colSpan={editable ? 6 : 5} className="text-center text-slate-400 py-6 text-sm">— no parts yet —</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Labor + Return Shipment</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>Labor Hours</Label><Input type="number" min="0" step="0.1" value={form.labor_hours} onChange={(e) => setForm({ ...form, labor_hours: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="ro-labor-hours" /></div>
          <div><Label>Labor Rate ($/hr)</Label><Input type="number" min="0" step="0.01" value={form.labor_rate} onChange={(e) => setForm({ ...form, labor_rate: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="ro-labor-rate" /></div>
          <div><Label>Return Carrier</Label><Input value={form.return_carrier} onChange={(e) => setForm({ ...form, return_carrier: e.target.value })} placeholder="UPS, FedEx…" disabled={!editable} className="rounded-sm" data-testid="ro-return-carrier" /></div>
          <div><Label>Return Tracking #</Label><Input value={form.return_tracking_number} onChange={(e) => setForm({ ...form, return_tracking_number: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="ro-return-tracking" /></div>
          <div className="col-span-2 md:col-span-4"><Label>Internal Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="ro-notes" /></div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardContent className="p-4 flex items-end justify-between gap-6 flex-wrap">
          <div className="text-sm space-y-1" data-testid="ro-totals">
            <div>Parts: <span className="mono font-semibold ml-2">${partsCost.toFixed(2)}</span></div>
            <div>Labor: <span className="mono font-semibold ml-2">${laborCost.toFixed(2)}</span></div>
            <div className="text-lg">Total: <span className="mono font-bold ml-2">${totalCost.toFixed(2)}</span>{form.warranty_covered && <span className="text-blue-600 text-xs ml-2">(waived — warranty)</span>}</div>
          </div>
          {editable && (
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ro-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (isNew ? 'Create Draft' : 'Save Changes')}
            </Button>
          )}
        </CardContent>
      </Card>

      {!isNew && (
        <EvidencePhotoGallery
          api={api}
          docType="repair_order"
          docId={id}
          evidenceType="work_evidence"
          title="Repair Evidence Photos"
          emptyHint="Capture before/after shots, damage close-ups, serial plate photos. They appear on the printed RO and PDF."
          testIdPrefix="ro-evidence"
        />
      )}
      {!isNew && ro && <DocHistoryPanel entityType="RepairOrder" docId={id} refreshKey={`${ro.status}-${ro.updated_at}`} />}
    </div>
  );
};

export default RepairOrderDetailPage;
